import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyEmail } from '../api/authService';
import { AuthContext } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';

const EmailVerificationPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);
    const { t } = useLocale();
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [error, setError] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            setStatus('error');
            setError(t('emailVerification.missingToken', 'No verification token found. Please check the link.'));
            return;
        }

        const verify = async () => {
            try {
                const data = await verifyEmail(token);
                setUser(data.user);
                setStatus('success');

                // Redirect based on onboarding status
                setTimeout(() => {
                    if (data.user.onboardingComplete) {
                        navigate('/dashboard');
                    } else {
                        navigate('/onboarding');
                    }
                }, 3000); // 3-second delay before redirect

            } catch (err) {
                setStatus('error');
                const message = err.response?.data?.msg || t('emailVerification.unknownError', 'An unknown error occurred.');
                setError(message);
            }
        };

        verify();
    }, [navigate, searchParams, setUser, t]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-black text-white px-4">
            <div className="max-w-md w-full text-center space-y-6 bg-[#121212] p-8 rounded-xl border border-gray-800 shadow-2xl">
                <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
                    {t('emailVerification.title', 'Email verification')}
                </h1>

                {status === 'verifying' && (
                    <div className="flex flex-col items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-4"></div>
                        <p className="text-gray-300">{t('emailVerification.verifying', 'Verifying your email, please wait...')}</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="rounded-md bg-green-900/50 p-4 border border-green-800">
                        <div className="flex">
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-200">
                                    {t('emailVerification.success', 'Verification successful. You are now signed in. Redirecting shortly...')}
                                </h3>
                            </div>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="space-y-4">
                        <div className="rounded-md bg-red-900/50 p-4 border border-red-800">
                            <div className="flex">
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-200">
                                        {error}
                                    </h3>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {t('emailVerification.goToLogin', 'Go to login')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailVerificationPage;
